// src/components/companion/SofiaIntro.jsx
//
// Sofia's introduction. Shown BEFORE the consent gate to anyone who has never
// met her, and re-openable afterwards from the "About Sofia" button on the
// chat header — "who is this and what do I do with her" is a question people
// have on the second visit too, not just the first.
//
// Follows Willow's introduction pattern: name, role, a sample of the actual
// voice, a plain description of what this is and isn't, then a clear way in.
// Nobody should be asked for their name and country before they know who is
// asking or why.

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle, Sparkles, ShieldCheck, Compass } from 'lucide-react';
import SofiaAvatar from './SofiaAvatar';

const CAN_HELP = [
  'Announcing an opinion and having it properly examined',
  'Getting the map of a debate: who said what, and why it mattered',
  'Building the strongest version of a view you can’t stand',
  'Testing today’s argument against the positions you’ve recorded at the forks',
  'Finding out what the move you just made is called (it usually has a name)',
];

const HOW_TO = [
  {
    title: 'Bring a belief, not a topic',
    body: '“I think punishment is really about revenge” gets you further than “tell me about justice”. She works on claims: yours, ideally.',
  },
  {
    title: 'Expect a clue before an answer',
    body: 'On the big contested questions she’d rather hand you a thought experiment than a conclusion. If you just want the lesson, say “teach me” and she’ll switch to the map and deliver it properly.',
  },
  {
    title: 'She names the prize',
    body: 'Reach an insight, hit a wall, or make a classic move, and she’ll tell you its proper name and who found it before you. You leave every conversation knowing more than you arrived with.',
  },
  {
    title: 'She’s read the whole course',
    body: 'Every module, the thinkers and worked examples, and the positions you’ve answered at the forks. Say “the chariot in module five” and she knows what you mean.',
  },
];

// The root uses initial={false} rather than an opacity entrance — this
// codebase's framer entrances have a history of stranding elements part-faded,
// and the whole app was converted away from initial={{opacity:0}} roots.
export default function SofiaIntro({ onContinue, returning = false, hasThread = false }) {
  return (
    <motion.div
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="max-w-2xl mx-auto bg-charcoal border border-white/10 rounded-2xl overflow-hidden"
    >
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-4 mb-5">
          <SofiaAvatar size={56} />
          <div>
            <p className="text-bright-gold text-sm italic">Your companion here</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Sofia</h1>
          </div>
        </div>

        {/* Her actual greeting, so the first thing you meet is the voice. */}
        <p className="text-lg sm:text-xl text-pearl italic leading-relaxed mb-5">
          “I&apos;m Sofia. I&apos;ll teach you plenty: names, history, the best arguments on every
          side. The one thing I won&apos;t hand over is what to conclude about the questions
          nobody&apos;s settled in two and a half thousand years. Those are yours. Tell me something
          you believe, or something you&apos;re stuck on.”
        </p>

        <p className="text-sm sm:text-base text-pearl/90 leading-relaxed mb-6">
          Profound Philosophy is a course in the great questions, and a course only gets you so far
          before you need someone to argue with. Sofia is that someone. She&apos;s generous with the
          history and the names, she&apos;ll hand you real treasure every time you talk, and she&apos;ll
          still make you walk the last mile yourself.
        </p>

        <div className="bg-gunmetal border border-white/10 rounded-xl p-4 sm:p-5 mb-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-bright-gold mb-3">
            <MessageCircle size={16} /> What people bring her
          </h2>
          <ul className="space-y-2">
            {CAN_HELP.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm sm:text-base text-pearl/90">
                <Sparkles size={15} className="flex-shrink-0 mt-1 text-gold" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-gunmetal border border-white/10 rounded-xl p-4 sm:p-5 mb-6">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-bright-gold mb-3">
            <Compass size={16} /> Getting the most out of her
          </h2>
          <ul className="space-y-3">
            {HOW_TO.map((item) => (
              <li key={item.title} className="text-sm sm:text-base">
                <p className="text-pearl font-medium">{item.title}</p>
                <p className="text-pearl/70 leading-relaxed">{item.body}</p>
              </li>
            ))}
          </ul>
          <p className="text-sm text-pearl/60 mt-4 pt-3 border-t border-white/10 leading-relaxed">
            Your conversation stays put if you wander off to read something she mentioned. Come back
            and it&apos;ll be where you left it. Start a fresh one whenever you like from the header.
          </p>
        </div>

        {/* Said plainly and up front rather than buried in consent small print. */}
        <div className="flex items-start gap-3 text-sm sm:text-base text-pearl/70 mb-7">
          <ShieldCheck size={18} className="flex-shrink-0 mt-0.5 text-pearl/50" />
          <p>
            She is not a therapist or counsellor, and philosophy is not therapy. If real weight
            arrives, she&apos;ll drop the method and say so. Conversations are stored in Australia and
            answered by Anthropic&apos;s Claude, which under Anthropic&apos;s commercial terms does not
            use them to train its models; they are never sold. You can read, export or delete
            everything she keeps, whenever you like.
          </p>
        </div>

        <button
          onClick={onContinue}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 bg-gold hover:bg-bright-gold text-gunmetal rounded-lg font-semibold text-base transition-colors"
        >
          {returning
            ? (hasThread ? 'Back to our conversation' : 'Back to Sofia')
            : 'Start talking with Sofia'}
          <ArrowRight size={18} />
        </button>
        {!returning && (
          <p className="text-xs text-pearl/60 text-center mt-3">
            Free: five messages a day. She&apos;ll ask a couple of things about you first.
          </p>
        )}
      </div>
    </motion.div>
  );
}
